import React, { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { format, startOfToday } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CalendarIcon, BarChart2, TrendingUp, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { StatisticsChart } from "@/components/StatisticsChart";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  transformDailyStats,
  transformWeeklyStats,
  transformMonthlyStats,
  formatApiParamsWithDateRange,
  calculateYAxisConfig,
} from "@/utils/statsTransform";
import { useNavigate } from "react-router-dom";
import { AppBar } from "@/components/ui/app-bar";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBackNavigation } from "@/hooks/use-back-navigation";
import { getBaseUrl } from "@/lib/api";
import { useUserPrivileges } from "@/context/UserPrivilegesContext";
import { PRIVILEGE } from "@/lib/privilegeConstants";

interface StatisticsPageProps {
  token: string;
}

// Type definitions for chart data
interface ChartData {
  label: string;
  value: number;
}

export function StatisticsPage({ token }: StatisticsPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { hasPrivilege } = useUserPrivileges();
  const showAllBranches = hasPrivilege(PRIVILEGE.ALL_BRANCHES);
  const showEodReportGraphs = hasPrivilege(PRIVILEGE.EOD_REPORT_GRAPHS);
  const [startDate, setStartDate] = useState<Date>(startOfToday());
  const [endDate, setEndDate] = useState<Date>(startOfToday());
  const [companyId, setCompanyId] = useState("all");
  const [locationId, setLocationId] = useState("all");
  const [userId, setUserId] = useState("all");
  const [chartType, setChartType] = useState<"Bar" | "SPLine" | "Area">("Bar");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Cleanup effect to clear cache when component unmounts
  useEffect(() => {
    console.log('StatisticsPage: Component mounted');
    return () => {
      console.log('StatisticsPage: Component unmounting, clearing cache');
      // Clear statistics specific queries when component unmounts
      queryClient.removeQueries({ queryKey: ['statistics-companies'] });
      queryClient.removeQueries({ queryKey: ['statistics-locations'] });
      queryClient.removeQueries({ queryKey: ['statistics-users'] });
      queryClient.removeQueries({ queryKey: ['dailyStats'] });
      queryClient.removeQueries({ queryKey: ['weeklyStats'] });
      queryClient.removeQueries({ queryKey: ['monthlyStats'] });
      queryClient.removeQueries({ queryKey: ['dailyStatsSummary'] });
    };
  }, [queryClient]);

  const baseUrl = getBaseUrl();
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

  // Fetch data from API with error handling and logging
  const fetchData = async (endpoint: string, params: any) => {
    try {
      console.log('[Statistics] Request', { url: `${baseUrl}/api/${endpoint}`, params });
      const response = await axios.get(`${baseUrl}/api/${endpoint}`, {
        headers: { Authorization: authToken },
        params,
      });
      
      if (!response.data?.data) {
        console.warn(`No data returned from ${endpoint}`);
        return [];
      }
      
      const result = response.data.data;
      console.log('[Statistics] Response', { endpoint, count: Array.isArray(result) ? result.length : 0, sample: Array.isArray(result) ? result[0] : result });
      return result;
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      return [];
    }
  };

  // Companies query
  const { data: companies } = useQuery({
    queryKey: ["statistics-companies"],
    queryFn: () => fetchData("Companies", {}),
  });

  // Locations query
  const { data: locations } = useQuery({
    queryKey: ["statistics-locations"],
    queryFn: () => fetchData("Locations", {}),
  });

  // Users query
  const { data: users } = useQuery({
    queryKey: ["statistics-users"],
    queryFn: () => fetchData("Users", {}),
  });

  // Debug logging for API data
  useEffect(() => {
    console.log('StatisticsPage: companies data:', companies);
    console.log('StatisticsPage: locations data:', locations);
    console.log('StatisticsPage: users data:', users);
  }, [companies, locations, users]);

  // When user does not have "All Branches" privilege, default company/location/user to first option instead of "All"
  useEffect(() => {
    if (showAllBranches) return;
    const companiesList = Array.isArray(companies) ? companies : [];
    const locationsList = Array.isArray(locations) ? locations : [];
    const usersList = Array.isArray(users) ? users : [];
    if (companyId === "all" && companiesList.length > 0) {
      setCompanyId(String(companiesList[0].Id));
    }
    if (locationId === "all" && locationsList.length > 0) {
      const loc = locationsList[0];
      setLocationId(loc.ID?.toString?.() ?? String(loc.ID));
    }
    if (userId === "all" && usersList.length > 0) {
      const u = usersList[0];
      setUserId(u.UserID ?? String(u.Id));
    }
  }, [showAllBranches, companyId, locationId, userId, companies, locations, users]);

  // Daily stats query
  const { data: dailyStats, isLoading: isLoadingDaily, error: dailyError } = useQuery({
    queryKey: ["dailyStats", startDate, endDate, companyId, locationId, userId],
    queryFn: async () => {
      try {
        const params = {
          ...formatApiParamsWithDateRange(startDate, endDate, { companyId, locationId, userId }),
          maxNo: 7,
        };
        const data = await fetchData("GetDailyStatsChart1", params);
        const transformed = transformDailyStats(data);
        if (!transformed || transformed.length === 0) {
          console.warn('No daily stats data available');
          return [];
        }
        return transformed.map(item => ({
          label: item.Date,
          value: item.InvoicedAmount
        }));
      } catch (error) {
        console.error('Error fetching daily stats:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  });

  // Weekly stats query
  const { data: weeklyStats, isLoading: isLoadingWeekly, error: weeklyError } = useQuery({
    queryKey: ["weeklyStats", startDate, endDate, companyId, locationId, userId],
    queryFn: async () => {
      try {
        const params = {
          ...formatApiParamsWithDateRange(startDate, endDate, { companyId, locationId, userId }),
          maxNo: 10,
        };
        const data = await fetchData("GetWeeklyStatsChart1", params);
        const transformed = transformWeeklyStats(data);
        if (!transformed || transformed.length === 0) {
          console.warn('No weekly stats data available');
          return [];
        }
        return transformed.map(item => ({
          label: item.Weekof,
          value: item.WeekTotal
        }));
      } catch (error) {
        console.error('Error fetching weekly stats:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  });

  // Monthly stats query
  const { data: monthlyStats, isLoading: isLoadingMonthly, error: monthlyError } = useQuery({
    queryKey: ["monthlyStats", startDate, endDate, companyId, locationId, userId],
    queryFn: async () => {
      try {
        const params = {
          ...formatApiParamsWithDateRange(startDate, endDate, { companyId, locationId, userId }),
          maxNo: 12,
        };
        const data = await fetchData("GetMonthlyStatsChart1", params);
        const transformed = transformMonthlyStats(data);
        if (!transformed || transformed.length === 0) {
          console.warn('No monthly stats data available');
          return [];
        }
        return transformed.map(item => ({
          label: item.Monthof,
          value: item.MonthTotal
        }));
      } catch (error) {
        console.error('Error fetching monthly stats:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  });

  // Daily stats summary query
  const { data: dailyStatsSummary, isLoading: isLoadingSummary, error: summaryError } = useQuery({
    queryKey: ["dailyStatsSummary", startDate, endDate, companyId, locationId, userId],
    queryFn: async () => {
      try {
        const params = {
          ...formatApiParamsWithDateRange(startDate, endDate, { companyId, locationId, userId }),
        };
        const data = await fetchData("GetDailyStatsSummary1", params);
        return data && data.length > 0 ? data[0] : null;
      } catch (error) {
        console.error('Error fetching daily stats summary:', error);
        return null;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  });

  // Debug logging
  useEffect(() => {
    console.group('Statistics Data Update');
    console.log('Start date:', format(startDate, "yyyy-MM-dd"));
    console.log('End date:', format(endDate, "yyyy-MM-dd"));
    console.log('API Params:', formatApiParamsWithDateRange(startDate, endDate, { companyId, locationId, userId }));
    console.log('Daily stats:', dailyStats);
    console.log('Weekly stats:', weeklyStats);
    console.log('Monthly stats:', monthlyStats);
    if (dailyError || weeklyError || monthlyError) {
      console.error('Errors:', {
        daily: dailyError,
        weekly: weeklyError,
        monthly: monthlyError,
      });
    }
    console.groupEnd();
  }, [startDate, endDate, dailyStats, weeklyStats, monthlyStats, companyId, locationId, userId, dailyError, weeklyError, monthlyError]);

  // Back button should always return to Reports tab (not Administrator or navigate(-1))
  const handleBackToReports = useCallback(() => {
    navigate('/?tab=reports');
  }, [navigate]);

  useBackNavigation({
    onBack: handleBackToReports,
    canGoBack: true,
    showExitConfirmation: false,
  });

  return (
    <div className={`${isMobile ? 'h-screen flex flex-col pt-16' : 'h-screen'}`}>
      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar 
          title="EOD Closure Report" 
          showBackButton={true}
          onBack={handleBackToReports}
          className="lg:hidden"
        />
      )}

      {/* Mobile Layout - Fixed Filters Section */}
      {isMobile ? (
        <>
          {/* Fixed Filters Section */}
          <div className="flex-shrink-0 px-4 pb-4">
            <Card className="bg-white/50 backdrop-blur-sm">
              <CardContent className="p-3">
                <div className="space-y-3">
                  <Collapsible
                    open={isFiltersOpen}
                    onOpenChange={setIsFiltersOpen}
                    className="space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-muted-foreground">Select Date Range & Filters</p>
                      <CollapsibleTrigger
                        onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                        className="rounded-md p-1.5 hover:bg-accent"
                      >
                        {isFiltersOpen ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="space-y-3">
                      <FilterContent
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        companyId={companyId}
                        setCompanyId={setCompanyId}
                        locationId={locationId}
                        setLocationId={setLocationId}
                        userId={userId}
                        setUserId={setUserId}
                        chartType={chartType}
                        setChartType={setChartType}
                        companies={companies}
                        locations={locations}
                        users={users}
                        showAllBranches={showAllBranches}
                        showChartTypeOptions={showEodReportGraphs}
                      />
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Scrollable Charts Section */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <div className="space-y-4">
              {showEodReportGraphs && (
                <>
                  <div className="w-full h-[240px] flex flex-col overflow-hidden">
                    <StatisticsChart
                      data={dailyStats || []}
                      xKey="label"
                      yKey="value"
                      title="Daily Statistics"
                      description={`Date range: ${format(startDate, "MMM dd")} - ${format(endDate, "MMM dd, yyyy")}`}
                      chartType={chartType}
                      isLoading={isLoadingDaily}
                      yAxisConfig={calculateYAxisConfig(dailyStats || [], 'value')}
                    />
                  </div>

                  <div className="w-full h-[240px] flex flex-col overflow-hidden">
                    <StatisticsChart
                      data={weeklyStats || []}
                      xKey="label"
                      yKey="value"
                      title="Weekly Statistics"
                      description={`Date range: ${format(startDate, "MMM dd")} - ${format(endDate, "MMM dd, yyyy")}`}
                      chartType={chartType}
                      isLoading={isLoadingWeekly}
                      yAxisConfig={calculateYAxisConfig(weeklyStats || [], 'value')}
                    />
                  </div>

                  <div className="w-full h-[240px] flex flex-col overflow-hidden">
                    <StatisticsChart
                      data={monthlyStats || []}
                      xKey="label"
                      yKey="value"
                      title="Monthly Statistics"
                      description={`Date range: ${format(startDate, "MMM dd")} - ${format(endDate, "MMM dd, yyyy")}`}
                      chartType={chartType}
                      isLoading={isLoadingMonthly}
                      yAxisConfig={calculateYAxisConfig(monthlyStats || [], 'value')}
                    />
                  </div>
                </>
              )}

              {/* Mobile Cards Section */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 text-center">Daily Statistics Summary</h2>
                
                {/* Section 1: Sales and Payments */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3">
                    <h3 className="text-base font-semibold text-white">Sales and Payments</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Total Invoices</span>
                      <span className="text-sm font-semibold text-blue-600">{Math.round(dailyStatsSummary?.TotalInvoices || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Refunded Invoices</span>
                      <span className="text-sm font-semibold text-red-600">{Math.round(dailyStatsSummary?.RefundedInvoices || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Total Sales</span>
                      <span className="text-sm font-semibold text-green-600">₹{Math.round(dailyStatsSummary?.InvoicedAmount || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Refunded Amount</span>
                      <span className="text-sm font-semibold text-red-600">₹{Math.round(dailyStatsSummary?.RefundAmount || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Payments Received</span>
                      <span className="text-sm font-semibold text-green-600">₹{Math.round(dailyStatsSummary?.CPTotal || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm font-medium text-gray-700">Dues</span>
                      <span className="text-sm font-semibold text-orange-600">₹{Math.round(dailyStatsSummary?.InvDue || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Section 2: Payments through Sales and Part Payments */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105">
                  <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-3">
                    <h3 className="text-base font-semibold text-white">Payments through Sales and Part Payments</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Advances and previous dues</span>
                      <span className="text-sm font-semibold text-purple-600">₹{Math.round(dailyStatsSummary?.DuesRecovered || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Cash in Hand</span>
                      <span className="text-sm font-semibold text-green-600">₹{Math.round(dailyStatsSummary?.CPCash || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Total Card Amount</span>
                      <span className="text-sm font-semibold text-blue-600">₹{Math.round(dailyStatsSummary?.CPCard || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Total Wallet Amount</span>
                      <span className="text-sm font-semibold text-indigo-600">₹{Math.round(dailyStatsSummary?.CPWallet || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Total Coupon Amount</span>
                      <span className="text-sm font-semibold text-yellow-600">₹{Math.round(dailyStatsSummary?.CPCoupon || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Total Credit Note Amount</span>
                      <span className="text-sm font-semibold text-pink-600">₹{Math.round(dailyStatsSummary?.CPCreditNote || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Total Cheque Amount</span>
                      <span className="text-sm font-semibold text-teal-600">₹{Math.round(dailyStatsSummary?.CPCheque || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm font-medium text-gray-700">Total Amount</span>
                      <span className="text-sm font-semibold text-purple-600">₹{Math.round(dailyStatsSummary?.CPTotal || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Section 3: Only through Sales */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105">
                  <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-4 py-3">
                    <h3 className="text-base font-semibold text-white">Only through Sales</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Cash payment through Invoices</span>
                      <span className="text-sm font-semibold text-green-600">₹{Math.round(dailyStatsSummary?.InvCash || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Card payment received</span>
                      <span className="text-sm font-semibold text-blue-600">₹{Math.round(dailyStatsSummary?.InvCard || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Wallet payment received</span>
                      <span className="text-sm font-semibold text-indigo-600">₹{Math.round(dailyStatsSummary?.InvWallet || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Coupon payment received</span>
                      <span className="text-sm font-semibold text-yellow-600">₹{Math.round(dailyStatsSummary?.InvCoupon || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Cheque payment received</span>
                      <span className="text-sm font-semibold text-teal-600">₹{Math.round(dailyStatsSummary?.InvCheque || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Redeemed through credit note</span>
                      <span className="text-sm font-semibold text-pink-600">₹{Math.round(dailyStatsSummary?.InvCreditNote || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-700">Due amount</span>
                      <span className="text-sm font-semibold text-orange-600">₹{Math.round(dailyStatsSummary?.InvDue || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm font-medium text-gray-700">Invoiced Amount (Incl Refunds)</span>
                      <span className="text-sm font-semibold text-emerald-600">₹{Math.round(dailyStatsSummary?.InvoicedAmount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* Desktop Layout - Fixed Filters with Scrollable Content */
        <>
          <div className="h-screen flex flex-col">
            {/* Desktop header section - Fixed */}
            <div className="flex-shrink-0 p-4 md:p-6">
              <div className="hidden md:flex items-center justify-between">
                {/* Desktop back button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden md:flex items-center gap-2"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Reports
                </Button>

                {/* Title */}
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 truncate">
                  EOD Closure Report
                </h1>
              </div>
            </div>

            {/* Fixed Filters Section */}
            <div className="flex-shrink-0 px-4 md:px-6 pb-4">
              <Card className="bg-white/50 backdrop-blur-sm">
                <CardContent className="p-3 md:p-4">
                  <div className="space-y-3">
                    {/* Desktop filters - always visible */}
                    <div className="hidden md:block">
                      <FilterContent
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        companyId={companyId}
                        setCompanyId={setCompanyId}
                        locationId={locationId}
                        setLocationId={setLocationId}
                        userId={userId}
                        setUserId={setUserId}
                        chartType={chartType}
                        setChartType={setChartType}
                        companies={companies}
                        locations={locations}
                        users={users}
                        showAllBranches={showAllBranches}
                        showChartTypeOptions={showEodReportGraphs}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Scrollable Charts and Tables Section */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 pb-4">
              <div className="space-y-6">
                {showEodReportGraphs && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                    <div className="w-full h-[440px]">
                      <StatisticsChart
                        data={dailyStats || []}
                        xKey="label"
                        yKey="value"
                        title="Daily Statistics"
                        description={`Date range: ${format(startDate, "MMM dd")} - ${format(endDate, "MMM dd, yyyy")}`}
                        chartType={chartType}
                        isLoading={isLoadingDaily}
                        yAxisConfig={calculateYAxisConfig(dailyStats || [], 'value')}
                      />
                    </div>

                    <div className="w-full h-[440px]">
                      <StatisticsChart
                        data={weeklyStats || []}
                        xKey="label"
                        yKey="value"
                        title="Weekly Statistics"
                        description={`Date range: ${format(startDate, "MMM dd")} - ${format(endDate, "MMM dd, yyyy")}`}
                        chartType={chartType}
                        isLoading={isLoadingWeekly}
                        yAxisConfig={calculateYAxisConfig(weeklyStats || [], 'value')}
                      />
                    </div>

                    <div className="w-full h-[440px] md:col-span-2 lg:col-span-1">
                      <StatisticsChart
                        data={monthlyStats || []}
                        xKey="label"
                        yKey="value"
                        title="Monthly Statistics"
                        description={`Date range: ${format(startDate, "MMM dd")} - ${format(endDate, "MMM dd, yyyy")}`}
                        chartType={chartType}
                        isLoading={isLoadingMonthly}
                        yAxisConfig={calculateYAxisConfig(monthlyStats || [], 'value')}
                      />
                    </div>
                  </div>
                )}

                {/* Responsive Tables Section */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">Daily Statistics Summary</h2>
                  
                  {/* Section 1: Sales and Payments */}
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                      <h3 className="text-lg font-semibold text-white">Sales and Payments</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <tbody>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Total Invoices</td>
                            <td className="px-6 py-4 text-right font-semibold text-blue-600">
                              {Math.round(dailyStatsSummary?.TotalInvoices || 0)}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Refunded Invoices</td>
                            <td className="px-6 py-4 text-right font-semibold text-red-600">
                              {Math.round(dailyStatsSummary?.RefundedInvoices || 0)}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Total Sales</td>
                            <td className="px-6 py-4 text-right font-semibold text-green-600">
                              ₹{Math.round(dailyStatsSummary?.InvoicedAmount || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Refunded Amount</td>
                            <td className="px-6 py-4 text-right font-semibold text-red-600">
                              ₹{Math.round(dailyStatsSummary?.RefundAmount || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Payments Received</td>
                            <td className="px-6 py-4 text-right font-semibold text-green-600">
                              ₹{Math.round(dailyStatsSummary?.CPTotal || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Dues</td>
                            <td className="px-6 py-4 text-right font-semibold text-orange-600">
                              ₹{Math.round(dailyStatsSummary?.InvDue || 0).toLocaleString()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Section 2: Payments through Sales and Part Payments */}
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
                      <h3 className="text-lg font-semibold text-white">Payments through Sales and Part Payments</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <tbody>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Advances and previous dues</td>
                            <td className="px-6 py-4 text-right font-semibold text-purple-600">
                              ₹{Math.round(dailyStatsSummary?.DuesRecovered || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Cash in Hand</td>
                            <td className="px-6 py-4 text-right font-semibold text-green-600">
                              ₹{Math.round(dailyStatsSummary?.CPCash || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Total Card Amount</td>
                            <td className="px-6 py-4 text-right font-semibold text-blue-600">
                              ₹{Math.round(dailyStatsSummary?.CPCard || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Total Wallet Amount</td>
                            <td className="px-6 py-4 text-right font-semibold text-indigo-600">
                              ₹{Math.round(dailyStatsSummary?.CPWallet || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Total Coupon Amount</td>
                            <td className="px-6 py-4 text-right font-semibold text-yellow-600">
                              ₹{Math.round(dailyStatsSummary?.CPCoupon || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Total Credit Note Amount</td>
                            <td className="px-6 py-4 text-right font-semibold text-pink-600">
                              ₹{Math.round(dailyStatsSummary?.CPCreditNote || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Total Cheque Amount</td>
                            <td className="px-6 py-4 text-right font-semibold text-teal-600">
                              ₹{Math.round(dailyStatsSummary?.CPCheque || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Total Amount</td>
                            <td className="px-6 py-4 text-right font-semibold text-purple-600">
                              ₹{Math.round(dailyStatsSummary?.CPTotal || 0).toLocaleString()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Section 3: Only through Sales */}
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4">
                      <h3 className="text-lg font-semibold text-white">Only through Sales</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <tbody>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Cash payment through Invoices</td>
                            <td className="px-6 py-4 text-right font-semibold text-green-600">
                              ₹{Math.round(dailyStatsSummary?.InvCash || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Card payment received</td>
                            <td className="px-6 py-4 text-right font-semibold text-blue-600">
                              ₹{Math.round(dailyStatsSummary?.InvCard || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Wallet payment received</td>
                            <td className="px-6 py-4 text-right font-semibold text-indigo-600">
                              ₹{Math.round(dailyStatsSummary?.InvWallet || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Coupon payment received</td>
                            <td className="px-6 py-4 text-right font-semibold text-yellow-600">
                              ₹{Math.round(dailyStatsSummary?.InvCoupon || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Cheque payment received</td>
                            <td className="px-6 py-4 text-right font-semibold text-teal-600">
                              ₹{Math.round(dailyStatsSummary?.InvCheque || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Redeemed through credit note</td>
                            <td className="px-6 py-4 text-right font-semibold text-pink-600">
                              ₹{Math.round(dailyStatsSummary?.InvCreditNote || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Due amount</td>
                            <td className="px-6 py-4 text-right font-semibold text-orange-600">
                              ₹{Math.round(dailyStatsSummary?.InvDue || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">Invoiced Amount (Incl Refunds)</td>
                            <td className="px-6 py-4 text-right font-semibold text-emerald-600">
                              ₹{Math.round(dailyStatsSummary?.InvoicedAmount || 0).toLocaleString()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// FilterContent component to avoid duplication
function FilterContent({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  companyId,
  setCompanyId,
  locationId,
  setLocationId,
  userId,
  setUserId,
  chartType,
  setChartType,
  companies,
  locations,
  users,
  showAllBranches,
  showChartTypeOptions,
}: {
  startDate: Date;
  setStartDate: (date: Date) => void;
  endDate: Date;
  setEndDate: (date: Date) => void;
  companyId: string;
  setCompanyId: (id: string) => void;
  locationId: string;
  setLocationId: (id: string) => void;
  userId: string;
  setUserId: (id: string) => void;
  chartType: "Bar" | "SPLine" | "Area";
  setChartType: (type: "Bar" | "SPLine" | "Area") => void;
  companies: any[];
  locations: any[];
  users: any[];
  showAllBranches: boolean;
  showChartTypeOptions?: boolean;
}) {
  const showChartTypeOptionsSection = showChartTypeOptions ?? false;
  const [startDateOpen, setStartDateOpen] = React.useState(false);
  const [endDateOpen, setEndDateOpen] = React.useState(false);

  const handleStartDateSelect = (date: Date | undefined) => {
    if (date) {
      setStartDate(date);
      setStartDateOpen(false); // Close the popover when date is selected
    }
  };

  const handleEndDateSelect = (date: Date | undefined) => {
    if (date) {
      setEndDate(date);
      setEndDateOpen(false); // Close the popover when date is selected
    }
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="flex flex-col">
          <Label className="text-xs font-medium text-muted-foreground mb-1">Start Date</Label>
          <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal h-8",
                  !startDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "PPP") : <span>Pick a start date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={handleStartDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex flex-col">
          <Label className="text-xs font-medium text-muted-foreground mb-1">End Date</Label>
          <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal h-8",
                  !endDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "PPP") : <span>Pick an end date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={handleEndDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="h-8">
            <SelectValue placeholder="Select Company" />
          </SelectTrigger>
          <SelectContent>
            {showAllBranches && <SelectItem value="all">All Companies</SelectItem>}
            {companies?.map((company: any) => (
              <SelectItem key={company.Id} value={company.Id.toString()}>
                {company.Name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={locationId} onValueChange={setLocationId}>
          <SelectTrigger className="h-8">
            <SelectValue placeholder="Select Location" />
          </SelectTrigger>
          <SelectContent>
            {showAllBranches && <SelectItem value="all">All Locations</SelectItem>}
            {locations?.map((location: any) => (
              <SelectItem key={location.ID} value={location.ID?.toString?.() ?? String(location.ID)}>
                {location.Name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={userId} onValueChange={setUserId}>
          <SelectTrigger className="h-8">
            <SelectValue placeholder="Select User" />
          </SelectTrigger>
          <SelectContent>
            {showAllBranches && <SelectItem value="all">All Users</SelectItem>}
            {users?.map((user: any) => (
              <SelectItem key={user.Id} value={user.UserID}>
                {user.UserID}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showChartTypeOptionsSection && (
        <div className="border-t pt-2">
          <RadioGroup
            value={chartType}
            onValueChange={(value: "Bar" | "SPLine" | "Area") => setChartType(value)}
            className="flex flex-wrap gap-2"
          >
            {[
              { value: "Bar", label: "Bar Chart", icon: BarChart2 },
              { value: "SPLine", label: "Line Chart", icon: TrendingUp },
              { value: "Area", label: "Area Chart", icon: TrendingUp },
            ].map(({ value, label, icon: Icon }) => (
              <div key={value} className="flex items-center space-x-2">
                <RadioGroupItem 
                  value={value} 
                  id={value}
                  className="h-5 w-5 border-2 border-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
                />
                <Label
                  htmlFor={value}
                  className={`flex items-center gap-1.5 cursor-pointer text-sm font-medium transition-colors ${
                    chartType === value 
                      ? 'text-blue-600' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Icon className={`h-3.5 w-3.5 ${
                    chartType === value ? 'text-blue-600' : 'text-gray-500'
                  }`} />
                  {label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      )}
    </div>
  );
}